import { Button } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import LinearProgress from "@mui/material/LinearProgress";
import Checkbox from "@saleor/components/Checkbox";
import MyTypography from "@saleor/components/MyTypography";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { NotRefOrderSortField } from "@saleor/createOrChangeNotRefContract/urls";
import Info from "assets/images/small-info.svg";
import classNames from "classnames";
import React, { useMemo } from "react";
import InlineSVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import useStyles from "./styles";

function SplitActionRow({
  classes,
  spanLength,
  onSubmitSplit,
  errors,
  splitRows
}) {
  const getErrorSummary = () => {
    let error = "";
    let otheQtyErr = "";
    let otheDateErr = "";
    let qtyError = 0;
    let dateError = 0;
    splitRows.forEach(row => {
      const rowErrorQuantity = errors[row?.indexNumber]?.quantity ?? "";
      const rowErrorRequestDate = errors[row?.indexNumber]?.requestDate ?? "";
      if (rowErrorQuantity === "โปรดระบุจำนวน") {
        qtyError++;
      }
      if (
        rowErrorQuantity &&
        rowErrorQuantity !== "โปรดระบุจำนวน" &&
        !rowErrorQuantity.startsWith("จัดส่งแล้ว")
      ) {
        otheQtyErr = rowErrorQuantity;
      }
      if (rowErrorRequestDate === "โปรดระบุวันที่") {
        dateError++;
      }
      if (rowErrorRequestDate && rowErrorRequestDate !== "โปรดระบุวันที่") {
        otheDateErr = rowErrorRequestDate;
      }
    });
    if (qtyError) {
      error = "โปรดระบุจำนวน";
      if (dateError) {
        error = error + " / Request Date";
      }
    } else if (dateError) {
      error = "โปรดระบุ Request Date";
    }
    if (otheDateErr) {
      error = otheDateErr;
    }
    if (otheQtyErr) {
      error = otheQtyErr;
    }
    return error;
  };
  return (
    <TableRow>
      <TableCell
        colSpan={4}
        align={"right"}
        style={{
          left: 0,
          position: "sticky",
          textAlign: "right",
          backgroundColor: "inherit",
          zIndex: 1
        }}
      >
        <div style={{ display: "flex", float: "right" }}>
          {errors && (
            <div className={classes.errorText}>{getErrorSummary()}</div>
          )}
          <Button
            id="saveSplitItem"
            data-testid="saveSplitItem"
            variant={"contained"}
            color={"secondary"}
            onClick={onSubmitSplit}
            className={classNames("defaultB", classes.smallBtn)}
          >
            บันทึก Split item
          </Button>
        </div>
      </TableCell>
      <TableCell colSpan={spanLength}></TableCell>
    </TableRow>
  );
}

interface EnhancedTableProps {
  columns: TableColumn[];
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowCount: number;
  handleSort?: (id) => void;
  headHeight: number;
  cellAlign?: "left" | "center" | "right" | "justify" | "inherit";
  sortDirection?: Map<string, "asc" | "desc">;
  selectAllLabel?: string;
  selectedStyles?: { style: React.CSSProperties; className: string };
  isViewMode?: boolean;
  disableAllCheckBox?: boolean;
  hideAllSelect?: boolean;
  currentAction?: "add" | "split" | "none" | "edit";
  disableSort: boolean;
  isLinesEmpty?: boolean;
  disableRequestDate?: boolean;
  loading?: boolean;
}

function EnhancedTableHead({
  onSelectAllClick,
  numSelected,
  rowCount,
  handleSort,
  columns,
  cellAlign,
  headHeight,
  sortDirection,
  selectAllLabel,
  selectedStyles,
  isViewMode,
  disableAllCheckBox,
  hideAllSelect,
  currentAction,
  disableSort,
  isLinesEmpty,
  disableRequestDate,
  loading
}: EnhancedTableProps) {
  const intl = useIntl();
  const classes = useStyles({});
  return useMemo(
    () => (
      <TableHead>
        <TableRow>
          {typeof onSelectAllClick === "function" && !hideAllSelect && (
            <TableCell
              key={"select"}
              style={{
                height: headHeight || 48,
                width: headHeight || 48,
                ...selectedStyles?.style
              }}
              className={selectedStyles?.className}
            >
              <span className={classes.delete}>
                {intl.formatMessage(
                  { defaultMessage: "{label}" },
                  { label: selectAllLabel || "Delete" }
                )}
              </span>
              <Checkbox
                style={{ height: 16 }}
                size={"small"}
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{ "aria-label": "select all items" }}
                disabled={disableAllCheckBox}
                id={"select-all-items"}
                data-testid={"select-all-items"}
              />
            </TableCell>
          )}
          {columns.map((headCell, index) => {
            let content: any = intl.formatMessage(
              { defaultMessage: "{label}" },
              { label: headCell.label }
            );
            if (headCell?.headerFormatter) {
              content = headCell?.headerFormatter(headCell, index, {
                isViewMode,
                currentAction,
                isLinesEmpty,
                disableRequestDate
              });
            }
            return (
              <>
                <TableCellHeader
                  key={headCell.id}
                  style={{
                    height: headHeight || 48,
                    ...headCell.style,
                    ...headCell.headerStyle
                  }}
                  className={classNames(headCell.className, {
                    [classes.childCell]: !!headCell.child
                  })}
                  {...(disableSort
                    ? {}
                    : {
                        direction: sortDirection?.get(headCell.id),
                        onClick: () =>
                          headCell.id === NotRefOrderSortField.itemNo
                            ? handleSort?.(headCell.id)
                            : null
                      })}
                  arrowPosition="right"
                  textAlign={
                    (headCell.align || cellAlign || "left") as
                      | "left"
                      | "center"
                      | "right"
                  }
                >
                  {content}
                </TableCellHeader>
                {!!headCell?.child && (
                  <TableCell
                    key={headCell?.child?.id}
                    align={headCell.child?.align || cellAlign || "left"}
                    style={{
                      height: headHeight || 48,
                      ...headCell.child?.style
                    }}
                    className={headCell.child?.className}
                  >
                    <MyTypography
                      variant="body3"
                      align={headCell.child?.align || cellAlign || "left"}
                    >
                      {intl.formatMessage(
                        { defaultMessage: "{childLabel}" },
                        { childLabel: headCell.child?.label }
                      )}
                    </MyTypography>
                  </TableCell>
                )}
              </>
            );
          })}
        </TableRow>
        <TableRow>
          <TableCell
            colSpan={columns?.length + 1}
            className={classes.tableHeaderLoading}
          >
            {loading && <LinearProgress />}
          </TableCell>
        </TableRow>
      </TableHead>
    ),
    [columns, sortDirection, numSelected, rowCount, currentAction, isLinesEmpty]
  );
}

interface IEnhancedTableCell {
  row: any;
  index: number;
  cellheight: number;
  cell: TableColumn;
  cellAlign: "left" | "right" | "inherit" | "center" | "justify";
  headHeight: number;
  classes: any;
  middleware: (formatter, row, cell, index, mode?: Imode) => JSX.Element;
  reRenderKey: string;
  isViewMode: boolean;
  isDisabled: boolean;
  error: any;
  sortDirection: "desc" | "asc";
  currentAction: "none" | "add" | "split" | "edit";
}

function EnhancedTableCell({
  row,
  cell,
  index,
  cellheight,
  cellAlign,
  headHeight,
  classes,
  middleware,
  reRenderKey,
  isViewMode,
  error,
  sortDirection,
  currentAction,
  isDisabled
}: IEnhancedTableCell) {
  return useMemo(() => {
    const labelId = `enhanced-table-checkbox-${index}`;
    let content: any = row[cell.id];
    if (!content) {
      content = <MyTypography variant="subtitle3">-</MyTypography>;
    }
    if (typeof cell.formatter === "function") {
      if (typeof middleware === "function") {
        content = middleware(cell.formatter, row, cell, index, {
          isViewMode,
          displayView: isViewMode || isDisabled,
          isDisabled,
          error,
          sortDirection,
          currentAction
        });
      } else {
        content = cell.formatter(row, cell, index, {
          isViewMode,
          displayView: isViewMode || isDisabled,
          isDisabled,
          error,
          sortDirection,
          currentAction
        });
      }
    }
    let childContent: any = null;
    if (cell.child) {
      childContent = row[cell.child?.id];
      if (!childContent) {
        childContent = <MyTypography variant="subtitle3">-</MyTypography>;
      }
      if (typeof cell.child?.formatter === "function") {
        if (typeof middleware === "function") {
          childContent = middleware(
            cell.child?.formatter,
            row,
            cell.child,
            index,
            {
              isViewMode,
              displayView: isViewMode || isDisabled,
              isDisabled,
              error,
              sortDirection,
              currentAction
            }
          );
        } else {
          childContent = cell.child?.formatter(row, cell.child, index, {
            isViewMode,
            displayView: isViewMode || isDisabled,
            isDisabled,
            error,
            sortDirection,
            currentAction
          });
        }
      }
    }
    return (
      <>
        <TableCell
          key={cell.id}
          align={cell.align || cellAlign || "left"}
          style={{ height: cellheight || 48, ...cell.style }}
          className={classNames(cell.className, {
            [classes.childCell]: !!cell?.child
          })}
          id={labelId}
          data-testid={`cell-${cell.id}`}
          scope="row"
          colSpan={cell.span}
        >
          {content}
        </TableCell>
        {!!cell?.child && (
          <TableCell
            align={cell.child?.align || cellAlign || "left"}
            style={{
              height: headHeight || 48,
              ...cell.child?.style
            }}
            className={cell.child?.className}
          >
            {childContent}
          </TableCell>
        )}
      </>
    );
  }, [
    row[cell.id],
    row[cell?.child?.id],
    row?.[reRenderKey],
    row?.isSplit,
    row?.splitOriginal,
    isViewMode,
    cell,
    row?.splitOriginal && row?.quantity <= 0,
    error,
    sortDirection,
    cell?.valueRerender?.(row),
    currentAction,
    isDisabled
  ]);
}

export default function AdvancedTable({
  tableKey,
  handleSort,
  className,
  rootClassName,
  columnsList,
  rowsData,
  headHeight,
  cellheight,
  cellAlign,
  sortDirection,
  selectProps,
  totalCount,
  middleware,
  onRowClick,
  borderRequired = false,
  dynamicRowStyle,
  hideSelect,
  reRenderKey,
  getIsLineViewMode,
  isViewMode,
  getIsDisabled,
  disableAllCheckBox,
  hideAllSelect,
  onSubmitSplit,
  errors,
  selectAllLabel,
  currentAction,
  disableSort = false,
  isLinesEmpty,
  disableRequestDate,
  loading = false,
  maxHeight
}: Readonly<AdvancedTable>) {
  const classes = useStyles({ maxHeight });
  return (
    <div className={classNames(classes.root, rootClassName)}>
      <div
        className={classNames(classes.tableRoot, {
          [classes.tableBorder]: borderRequired
        })}
      >
        <Table
          data-testid="advanced-table"
          className={className}
          aria-labelledby="tableTitle"
          aria-label="Advanced table"
          stickyHeader
        >
          {loading && <div className={classes.tableLoading} />}
          {rowsData?.length > 0 ? (
            <>
              <EnhancedTableHead
                numSelected={selectProps?.selected?.length}
                onSelectAllClick={selectProps?.onSelectAll}
                handleSort={handleSort}
                columns={columnsList}
                rowCount={totalCount}
                headHeight={headHeight}
                cellAlign={cellAlign}
                sortDirection={sortDirection}
                selectedStyles={{
                  style: selectProps?.style,
                  className: selectProps?.className
                }}
                isViewMode={isViewMode}
                disableAllCheckBox={disableAllCheckBox}
                hideAllSelect={hideAllSelect}
                selectAllLabel={selectAllLabel}
                currentAction={currentAction}
                disableSort={disableSort}
                isLinesEmpty={isLinesEmpty}
                disableRequestDate={disableRequestDate}
                loading={loading}
              />
              <TableBody>
                {columnsList.length > 0 &&
                  rowsData.map((row, index) => {
                    const isSelected = (name: string) =>
                      selectProps?.selected?.indexOf(name) !== -1;
                    const isItemSelected = isSelected(row?.[tableKey]);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    const isDisabled = getIsDisabled?.(row) || false;
                    const isLineViewMode =
                      getIsLineViewMode?.(row) || isViewMode || false;
                    const showSubmitSplit =
                      row?.isSplit && !rowsData?.[index + 1]?.isSplit;
                    return (
                      <>
                        <TableRow
                          style={{
                            height: cellheight || 48,
                            padding: 0
                          }}
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row?.[tableKey]}
                          selected={isItemSelected}
                          onClick={() => onRowClick?.(row)}
                          className={classNames(dynamicRowStyle?.(row), {
                            [classes.rowDisable]: isDisabled
                          })}
                        >
                          {typeof selectProps?.onSelect === "function" &&
                            !hideAllSelect && (
                              <TableCell
                                onClick={() =>
                                  selectProps?.onSelect?.(row?.[tableKey])
                                }
                                style={{
                                  height: cellheight || 48,
                                  width: cellheight || 48,
                                  pointerEvents:
                                    disableAllCheckBox || isDisabled
                                      ? "none"
                                      : "inherit",
                                  ...selectProps?.style
                                }}
                                className={selectProps?.className}
                              >
                                {!hideSelect?.(row) && (
                                  <Checkbox
                                    size="small"
                                    checked={isItemSelected}
                                    inputProps={{
                                      "aria-labelledby": labelId
                                    }}
                                    disabled={disableAllCheckBox || isDisabled}
                                  />
                                )}
                              </TableCell>
                            )}
                          {columnsList.map(cell => (
                            <EnhancedTableCell
                              key={row?.indexNumber + cell.id}
                              row={row}
                              cell={cell}
                              cellAlign={cellAlign}
                              cellheight={cellheight}
                              classes={classes}
                              index={index}
                              error={errors?.[row?.indexNumber]?.[cell.id]}
                              headHeight={headHeight}
                              middleware={middleware}
                              reRenderKey={reRenderKey}
                              isViewMode={isLineViewMode}
                              isDisabled={isDisabled}
                              sortDirection={
                                cell?.id === "materialCode"
                                  ? sortDirection.get("itemNo")
                                  : "asc"
                              }
                              currentAction={currentAction}
                            />
                          ))}
                        </TableRow>
                        {showSubmitSplit && (
                          <SplitActionRow
                            classes={classes}
                            onSubmitSplit={onSubmitSplit}
                            spanLength={columnsList?.length}
                            splitRows={rowsData?.filter(
                              row => row?.isSplit && !row?.parentItemNo
                            )}
                            errors={errors}
                          />
                        )}
                      </>
                    );
                  })}
              </TableBody>
            </>
          ) : (
            <div className={classes.noItemBody}>
              <InlineSVG src={Info} />
              <span style={{ paddingLeft: "8px", marginTop: "-2px" }}>
                ยังไม่มีรายการสินค้า โปรดกด “เพิ่มรายการสินค้า”
              </span>
            </div>
          )}
        </Table>
      </div>
    </div>
  );
}
